import { ASSET_CLASS_ORDER } from '@ddt-web/common';
import { CellData, CellUtil, TableData } from '@ddt-web/data-service';
import { NumberUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

export class HoldingsTableUtil {
	/**
	 * Show holding level Alerts based on the security
	 *
	 * @static
	 * @param {Array<Map<string, string>>} substitutionsAlerts
	 * @param {string} securityId
	 * @param {number} portfolioIndex
	 * @returns {boolean}
	 * @memberof HoldingsTableUtil
	 */
	public static showSubstitutedFromAlert(substitutionsAlertMap: Map<string, string>, securityId: string): boolean {
		return (
			!isEmpty(substitutionsAlertMap) &&
			// Only if the substitution alert map contains the security
			substitutionsAlertMap.has(securityId)
		);
	}

	public static populateCategoryIndexMap(tableData: TableData, rowsToHide: Array<number> = [], assetClassOrder: Array<string> = ASSET_CLASS_ORDER): Map<any, any> {
		const categoryIndexMap = new Map();
		const assetClassColIndex = tableData?.cells[0]?.findIndex(column => get(column, 'config.id') === 'assetClass');
		tableData.cells.forEach((row, index) => {
			if (!rowsToHide.includes(index)) {
				if (!categoryIndexMap.has(row[assetClassColIndex].formattedValue)) {
					const indexArray: number[] = new Array<number>();
					indexArray.push(index);
					categoryIndexMap.set(row[assetClassColIndex].formattedValue, indexArray);
				} else {
					categoryIndexMap.get(row[assetClassColIndex].formattedValue).push(index);
				}
			}
		});
		return HoldingsTableUtil.sortCategory(categoryIndexMap, assetClassOrder);
	}

	/**
	 * Function to sort categories in category map. It can also handle the case if
	 * we have new categories/asset classes in the future or asset class name changes/mismatch, it will append the new
	 * categories to the bottom.
	 *
	 * @param {Map} categoryMap
	 * @returns {Map}
	 */
	public static sortCategory(categoryMap: Map<any, any>, assetClassOrder: Array<string> = ASSET_CLASS_ORDER): Map<any, any> {
		const result = new Map();
		const mapSize = categoryMap.size;
		let count = 0;
		assetClassOrder.forEach(category => {
			if (categoryMap.has(category)) {
				result.set(category, categoryMap.get(category));
				categoryMap.delete(category);
				count++;
			}
		});
		if (count < mapSize) {
			categoryMap.forEach((value, key) => result.set(key, value));
		}
		return result;
	}

	/**
	 * Returns the category weight map for the provided tableData and categoryIndexMap.
	 * If excludePortfolioIndices is provided, the weight is set to '' for the portfolio indexes provided.
	 * 
	 * @param tableData 
	 * @param categoryIndexMap 
	 * @param excludePortfolioIndices 
	 */
	public static populateCategoryWeightMap(tableData: TableData, categoryIndexMap: Map<any, any>, excludePortfolioIndices?: Array<number>): Map<any, any> {
		const categoryWeightMap = new Map();
		categoryIndexMap.forEach((value, key) => {
			const weightSumsArray = [];
			value.forEach(rowIndex => {
				let index = 0;
				tableData.headersX.forEach((portfolio, i) => {
					if (get(portfolio, 'config.id') === 'name' && tableData.cells[rowIndex][i]) {
						if (excludePortfolioIndices?.includes(i)) {
							weightSumsArray[index] = '';
						} else {
							if (!weightSumsArray[index]) { weightSumsArray[index] = 0; }
							if (tableData.cells[rowIndex][i].rawValue) {
								weightSumsArray[index] = (+weightSumsArray[index]) + (+(tableData.cells[rowIndex][i].rawValue));
							}
						}
						index++;
					}
				});
			});

			categoryWeightMap.set(key, weightSumsArray);
		});

		return categoryWeightMap;
	}

	public static formatWeight(value: number, formatter?: Function): string {
		const holdingsConfig = new HoldingsConfig();
		if (formatter) {
			return formatter(value);
		}
		return value > 0 ? NumberUtil.formatNumber(value, holdingsConfig.WEIGHT_DECIMAL) + '%' : '-';
	}

	public static shouldDisplaySecurityParametricIndicators(cells: CellData[]): boolean {
		const parametricIndicatorValue = CellUtil.getById<CellData>(cells, 'parametricDateIndicator');
		return Boolean(parametricIndicatorValue?.rawValue);
	}
}

class HoldingsConfig {
	public readonly WEIGHT_DECIMAL: number = 2;
}
